import { Component, createRef } from 'preact';
import { CARD_CORNER_RATIO } from '../lib/cardCorner';
import CardFoilScene from './CardFoilScene';
import { getCanvasScale, onCanvasScaleChange } from '../utils/uiScale';
import { isDevBuild } from '../utils/devMode';
import { isPhoneLayout } from '../utils/platform';

// Headroom around the foil canvas, expressed at DESIGN scale (--canvas-scale 1)
// and multiplied by the live canvas scale before use. See _overflowPx/_vpadPx.
//
// These MUST scale. Everything they have to cover is in cqw and therefore grows
// with the canvas: the tile itself, its hover lift/tilt, and the scroll
// container's pl-10/pr-10 padding. Left as raw px they were sized for a laptop
// (scale ~0.85-1.0) and fell far short on a large external monitor (scale 2+),
// where a hovered card is twice the size but the canvas still only had 56px of
// slack. So the card clipped at the canvas edge and read as spilling out of the
// grid. This is the hardcoded-px trap the UI scaling rules in CLAUDE.md warn
// about; the values below are the only px constants left in this file.
const FOIL_OVERFLOW = 56;
// Top/bottom headroom above the first row / below the last row so a foil card
// whose CENTER-pivot hover lift would otherwise overflow past the scroll
// container's clip has room to grow inward instead. Only visible as padding at
// the scroll extremes.
const FOIL_VPAD = 48;

// Phones keep one off-screen row instead of three: each buffered row is a
// full row of decoded card <img>s (plus foil quads), and iOS Safari's per-tab
// memory ceiling is a fraction of a desktop browser's.
const BUFFER_ROWS = isPhoneLayout() ? 1 : 3;

// Grid-scale foil shader trims: POM relief is ~2px and invisible at tile size,
// glint cells (uGlintDensity=150) are sub-pixel here, sparkle dots (cellSize ≈
// 1px) alias to noise. Dropping these three eliminates the dominant fragment
// cost (POM's 10-tap loop, the 3-octave glint hash, the sparkle hash) so the
// scene redraws cheaply each scroll frame while the foil still reads correctly
// from GGX + matcap env + iridescence + thin-film + emboss + rest holo.
const GRID_FOIL_OVERRIDES = { pomOn: 0, glintOn: 0, sparkleOn: 0 };

// Mount-time entrance cascade: per-tile delay step and the index cap the
// stagger stops growing at (tiles past the cap land together. The sweep
// reads on the first rows; a full-viewport-long tail would just feel slow).
const ENTRANCE_STEP_MS = 18;
const ENTRANCE_STEP_CAP = 20;
// Mirrors `.stagger-fade-in`'s duration in app.css. Used only to know when the
// last tile has settled so the cascade can latch itself off.
const ENTRANCE_TILE_MS = 240;

// Virtualized WebGL card grid. The DOM tile grid is a plain CSS Grid with
// padding-based windowing (paddingTop/paddingBottom reserve the unrendered
// rows), so the browser scrolls tiles natively. No per-frame JS transform.
//
// The WebGL foil canvas is a CONTENT CHILD of the same scroll container (an
// absolutely-positioned overlay anchored at the windowed region's content-y),
// NOT a viewport-pinned sticky layer. Because it lives inside the scrolled
// content it rides native scroll on the SAME compositor pass as the tiles, so
// the foil is frame-locked to its card. No main-thread rAF chasing scrollTop
// (which lags threaded/momentum scroll on macOS → the foil trailing the tiles).
//
// The known failure mode of "canvas rides native scroll." A large WebGL layer
// drifting PROGRESSIVELY off the grid on macOS. Is defeated by keeping the
// canvas viewport-sized and RE-ANCHORING it to the windowed region on every row
// crossing (mirrors the tile padding-virtualization). Any per-pass compositor
// error is reset to zero each window, so it never accumulates. Quads are placed
// in content space relative to the anchor and only change when the window does;
// the rAF loop is left to drive hover/tilt easing, not scroll tracking.
export default class CardGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Virtualization state. `cols` and `rowHeight` are 0 until we measure a
      // rendered tile; until then we render an initial batch so there's
      // something to measure.
      cols: 0,
      rowHeight: 0,
      scrollTop: 0,
      viewport: 0,
      // Width of the scroll viewport (= foil scene canvas CSS width) and the
      // geometry of a tile + its card-image area, all measured from the DOM in
      // measureGrid. Drive the foil quad rects so the canvas art lands exactly
      // on each tile's image hole.
      viewportW: 0,
      tileWidth: 0,
      gap: 18,
      imgOffX: 0,
      imgOffY: 0,
      imgW: 0,
      imgH: 0,
      // Live --canvas-scale. The foil headroom is derived from it, and a change
      // (window resize OR a monitor handoff that flips the auto aspect) also has
      // to re-measure, because every tile dimension is cqw-derived.
      canvasScale: getCanvasScale(),
      // Entrance cascade. `entranceStagger` (a base delay in ms) opts in;
      // `entranceReady` is the cue to actually run it, so the grid can mount
      // and measure behind a loading gate and animate only once its art is
      // warm. While true, cells get a short per-tile staggered rise.
      // Flips false once the cascade has finished so later renders drop the
      // animation classes (scrolled-in cells must not re-animate).
      entering: false,
      // Latches so the cascade runs at most once per mount.
      entranceDone: !props.entranceStagger,
    };
    this.scrollRef = createRef();
    this.gridRef = createRef();
    this.contentRef = createRef();
    this.foilSceneRef = createRef();
    // Live hover tilt per foil card key, written by renderTile's onTilt and read
    // by the rAF loop when it builds the foil quad array. Defaults to rest.
    this._tilts = new Map();
    // Foil cell DOM nodes (ref callbacks) + their cached content-y (offsetTop
    // within the relative wrapper). Footers vary in height, so grid rows aren't
    // uniform. The quad y is read from the REAL tile position, refreshed once
    // per commit (never in the rAF loop, which would thrash layout each frame).
    this._foilCells = new Map();
    this._foilTop = new Map();
    this._loopRaf = null;
    this._scrollRaf = null;
    this._resizeObs = null;
    this._lastWindowRow = 0;
  }

  componentDidMount() {
    const container = this.scrollRef.current;
    if (container && typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => this.measureGrid());
      this._resizeObs.observe(container);
    }
    // Re-measure on canvas-scale change as well as on container resize. The
    // ResizeObserver alone is not enough: dragging the window to a display with
    // a different aspect flips the auto aspect via the display SSE feed, which
    // rescales every cqw dimension inside a container whose own box may not have
    // changed enough to trip the observer.
    this._unsubScale = onCanvasScaleChange((canvasScale) => {
      this.setState({ canvasScale }, this.measureGrid);
    });
    this.measureGrid();
    this._refreshFoilTops();
    this._startFoilLoop();
    if (this.props.entranceStagger && this.props.entranceReady) this._startEntrance();
  }

  // The cascade self-terminates: once the last tile has landed, `entering` goes
  // false so subsequent renders drop the animation classes entirely. Without
  // this, tiles scrolled into the window later would replay the entrance.
  _startEntrance() {
    if (this._entranceTimer || this.state.entranceDone) return;
    const base = typeof this.props.entranceStagger === 'number' ? this.props.entranceStagger : 0;
    this.setState({ entering: true });
    this._entranceTimer = setTimeout(() => {
      this._entranceTimer = null;
      this.setState({ entering: false, entranceDone: true });
    }, base + ENTRANCE_STEP_CAP * ENTRANCE_STEP_MS + ENTRANCE_TILE_MS + 100);
  }

  // Foil canvas headroom in LIVE px: the design-scale constants above times the
  // current canvas scale. Both the quad rects (_buildFoilCards) and the canvas
  // element (render) must read these, never the raw constants, or the quads and
  // the buffer they live in disagree.
  _overflowPx() {
    return Math.round(FOIL_OVERFLOW * (this.state.canvasScale || 1));
  }

  _vpadPx() {
    // A host that never scrolls (the profile showcase) trades the deep
    // scroll-extreme headroom for room its rows can use.
    return Math.round((this.props.vpad ?? FOIL_VPAD) * (this.state.canvasScale || 1));
  }

  // Cache each mounted foil cell's real content-y (offsetTop within the relative
  // wrapper). Run once per commit so quad y's track the ACTUAL DOM layout rather
  // than a uniform row*rowHeight model that drifts when footers vary in height.
  _refreshFoilTops = () => {
    for (const [key, node] of this._foilCells) {
      if (node && node.isConnected) this._foilTop.set(key, node.offsetTop);
    }
  };

  componentDidUpdate(prevProps) {
    // First-paint measurement: once tiles exist in the DOM, capture column
    // count + row height so subsequent renders can window.
    if (this.state.cols === 0 || this.state.rowHeight === 0) {
      this.measureGrid();
    }

    // Switching grid size changes tile width → tile height (cards are
    // aspect-ratio locked). Container size is unchanged, so the ResizeObserver
    // wouldn't fire on its own. Re-measure explicitly.
    if (prevProps.gridSize !== this.props.gridSize) {
      this.measureGrid();
    }

    if (prevProps.refreshKey !== this.props.refreshKey) {
      this._animateRefresh();
    }

    // The grid mounts behind the deck editor's loading gate, so the tiles are
    // laid out and their art is warm before any of it is visible. The gate
    // lifting flips entranceReady. That is the cue to run the cascade, not
    // mount.
    if (!prevProps.entranceReady && this.props.entranceReady && this.props.entranceStagger) {
      this._startEntrance();
    }

    // Re-window lands the foil canvas element at a new anchor `top` in this
    // same commit. Re-cache the real tile positions, then redraw the scene
    // SYNCHRONOUSLY (before the browser paints) so the buffer's quads match the
    // new anchor + layout in the same frame. Without this the redraw waits for
    // the next rAF tick, so for one frame the stale buffer sits at the moved
    // top. A card "copy" flickering in offset.
    this._refreshFoilTops();
    const scene = this.foilSceneRef.current;
    if (scene) { scene.setCards(this._buildFoilCards()); scene.frame(0); }
  }

  componentWillUnmount() {
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
    if (this._loopRaf) {
      cancelAnimationFrame(this._loopRaf);
      this._loopRaf = null;
    }
    if (this._resizeObs) {
      this._resizeObs.disconnect();
      this._resizeObs = null;
    }
    if (this._entranceTimer) {
      clearTimeout(this._entranceTimer);
      this._entranceTimer = null;
    }
    this._unsubScale?.();
  }

  // Continuous rAF dedicated to the foil scene canvas. It does NOT track scroll
  // The canvas is a scrolled content child, so the compositor moves its quads
  // in lockstep with the tiles. The loop exists to drive hover/tilt EASING: each
  // tick it rebuilds the quad rects (content-anchored positions + live tilt from
  // the _tilts map) and calls frame(dt). The scene gates its own redraw via
  // _dirty / easing: at rest the quads are identical -> no redraw; a hover tilt
  // changes the rotation/nudge -> one cheap redraw per frame until it settles.
  _startFoilLoop = () => {
    if (this._loopRaf) return;
    this._loopLast = 0;
    const loop = (t) => {
      this._loopRaf = requestAnimationFrame(loop);
      const dt = this._loopLast ? (t - this._loopLast) / 1000 : 0;
      this._loopLast = t;
      const scene = this.foilSceneRef.current;
      if (!scene) return;
      const cards = this._buildFoilCards();
      // Idle gate: a grid with no foil tiles in the window (the common case in
      // the deck builder) would otherwise rebuild an empty quad array and poke
      // the scene 60x/s while the user scrolls. One trailing pass runs when the
      // count drops to zero so the scene clears what it last drew.
      if (!cards.length && !this._lastFoilCount) return;
      this._lastFoilCount = cards.length;
      scene.setCards(cards);
      scene.frame(dt);
    };
    this._loopRaf = requestAnimationFrame(loop);
  };

  // Map the currently-mounted FOIL tiles (computed in render → this._visibleFoil)
  // to canvas-local quad rects. The canvas is anchored at the windowed region's
  // content-y (this._foilAnchorTop) and rides native scroll, so a quad's y is
  // its cell's content-y MINUS the anchor (plus the FOIL_OVERFLOW inset). A
  // fixed value that only changes when the window re-anchors, never per scroll
  // frame. The compositor carries the already-drawn quads with the tiles.
  _buildFoilCards = () => {
    const list = this._visibleFoil;
    if (!list || !list.length) return [];
    const { cols, rowHeight, tileWidth, gap, imgOffX, imgOffY, imgW, imgH, entering } = this.state;
    if (!cols || !rowHeight || !tileWidth) return [];
    const anchorTop = this._foilAnchorTop || 0;
    const overflow = this._overflowPx();
    const vpad = this._vpadPx();
    // The quad rounds itself (the canvas can't rely on the tile's CSS clip) to
    // the app-wide card corner, a fraction of the quad width.
    const cornerRadius = CARD_CORNER_RATIO;
    const out = [];
    for (const f of list) {
      const row = Math.floor(f.gi / cols);
      const col = f.gi % cols;
      const colX = col * (tileWidth + gap);
      // Real cell top from the cached DOM offsetTop (robust to variable footer
      // heights); fall back to the uniform model before the first measure.
      const cellTop = this._foilTop.has(f.key)
        ? this._foilTop.get(f.key)
        : vpad + row * rowHeight;
      const tilt = this._tilts.get(f.key) || { mx: 50, my: 50, fromCenter: 0 };
      out.push({
        key: f.key,
        imageUrl: f.imageUrl,
        zones: f.zones,
        x: colX + imgOffX + overflow,
        y: cellTop - anchorTop + imgOffY + overflow,
        w: imgW,
        h: imgH,
        mx: tilt.mx,
        my: tilt.my,
        fromCenter: tilt.fromCenter,
        rotateX: tilt.rotateX || 0,
        rotateY: tilt.rotateY || 0,
        rotateZ: tilt.rotateZ || 0,
        scale: tilt.scale || 1,
        nudgeX: tilt.nudgeX || 0,
        nudgeY: tilt.nudgeY || 0,
        hovering: !!tilt.hovering,
        cornerRadius,
        // Foil art lives on this shared canvas, not in the cell, so the cell's
        // entrance animation cannot fade it. Reading the cell's LIVE computed
        // opacity and pushing it into the quad's shader is what keeps the two in
        // lockstep: same clock, same easing, same per-tile delay, by
        // construction rather than by a JS timeline that would have to
        // re-derive all three and drift. Off the entrance path this is skipped
        // entirely and quads stay fully opaque.
        opacity: entering ? this._cellOpacity(f.key) : 1,
      });
    }
    return out;
  };

  // Live opacity of a foil cell mid-entrance. Reads the animated value off the
  // element; cheap, and opacity never invalidates layout so it cannot thrash.
  _cellOpacity(key) {
    const el = this._foilCells.get(key);
    if (!el) return 1;
    const v = parseFloat(getComputedStyle(el).opacity);
    return Number.isFinite(v) ? v : 1;
  }

  // Measure cols, row height, tile width, gap, the viewport box and the
  // card-image area within a tile. All from a hidden `.card-grid` probe
  // (gridRef) that mirrors the live CSS (cols from grid-template-columns, gap
  // from columnGap, tileWidth from the first cell). These drive the foil quad
  // rects so the canvas art lands on each tile's image hole. Recomputed on
  // mount, until measured, on resize, and on grid-size change.
  measureGrid = () => {
    const container = this.scrollRef.current;
    const grid = this.gridRef.current;
    if (!container || !grid) return;
    const firstTile = grid.firstElementChild;
    if (!firstTile) return;

    const tileHeight = firstTile.offsetHeight;
    const tileWidth = firstTile.offsetWidth;
    if (!tileHeight || !tileWidth) return;

    const gs = window.getComputedStyle(grid);
    const cols = gs.gridTemplateColumns.split(' ').filter(Boolean).length;
    if (!cols) return;
    const gap = parseFloat(gs.columnGap || gs.gap) || 0;
    const rowHeight = tileHeight + gap;

    // Card-image area = the inner face (.deck-card-tile-inner) measured relative
    // to the tile root. The image fills the inner (object-cover), so this rect
    // is the foil quad's px box; usually 0,0,tileWidth,tileHeight but measured
    // so any future tile chrome can't desync the quad.
    const inner = firstTile.querySelector('.deck-card-tile-inner') || firstTile;
    const tRect = firstTile.getBoundingClientRect();
    const iRect = inner.getBoundingClientRect();
    const imgOffX = iRect.left - tRect.left;
    const imgOffY = iRect.top - tRect.top;
    const imgW = iRect.width;
    const imgH = iRect.height;
    const viewport = container.clientHeight;
    // Content-box width (the grid spans this, excluding the scroll container's
    // horizontal padding). This is the canvas CSS width and the foil scene's
    // coordinate origin span.
    const viewportW = grid.clientWidth;

    if (
      this.state.cols !== cols ||
      this.state.rowHeight !== rowHeight ||
      this.state.viewport !== viewport ||
      this.state.viewportW !== viewportW ||
      this.state.tileWidth !== tileWidth ||
      this.state.gap !== gap ||
      this.state.imgOffX !== imgOffX ||
      this.state.imgOffY !== imgOffY ||
      this.state.imgW !== imgW ||
      this.state.imgH !== imgH
    ) {
      // Emit after the measurement commits so a consumer (e.g. an alphabet
      // index) gets cols/rowHeight at the current scroll row without waiting for
      // the first scroll. Matching the legacy first-paint measure behavior.
      this.setState({ cols, rowHeight, viewport, viewportW, tileWidth, gap, imgOffX, imgOffY, imgW, imgH }, this._emitViewChange);
    }

    // TEMPORARY DIAGNOSTIC (dev builds only). Remove once the foil-quad
    // mismatch on wide displays is fixed. Every foil quad's size and position
    // comes from the hidden probe above, while the DOM tiles are laid out by
    // CSS. If those two ever disagree the foil cards render at the wrong size /
    // offset while the non-foil tiles look correct, which is precisely the
    // reported symptom. Log both so the divergence is visible instead of guessed.
    if (isDevBuild()) {
      const realGrid = container.querySelector('div[style*="relative"] > .card-grid');
      const realCell = realGrid?.firstElementChild;
      const realInner = realCell?.querySelector('.deck-card-tile-inner');
      const r = realInner?.getBoundingClientRect();
      const realGridRect = realGrid?.getBoundingClientRect();
      const probeGridRect = grid.getBoundingClientRect();
      console.log('[foil-measure]', JSON.stringify({
        win: [window.innerWidth, window.innerHeight],
        dpr: window.devicePixelRatio,
        canvasScale: this.state.canvasScale,
        mediaQuery2200: window.matchMedia('(min-width: 2200px)').matches,
        probe: {
          cols, gap, tileWidth, tileHeight,
          imgW, imgH, imgOffX, imgOffY,
          gridW: Math.round(probeGridRect.width),
          cssCols: window.getComputedStyle(grid).gridTemplateColumns,
        },
        real: {
          gridW: realGridRect ? Math.round(realGridRect.width) : null,
          cellW: realCell ? realCell.offsetWidth : null,
          cellH: realCell ? realCell.offsetHeight : null,
          innerW: r ? Math.round(r.width) : null,
          innerH: r ? Math.round(r.height) : null,
          cssCols: realGrid ? window.getComputedStyle(realGrid).gridTemplateColumns : null,
        },
        viewportW,
      }, null, 1));
    }
  };

  // Filter/sort changed (refreshKey bumped): settle the fresh result set in
  // with a quick fade + rise. Runs on the CONTENT WRAPPER. The WebGL foil
  // canvas is a child, so foil quads stay frame-locked to their DOM tiles;
  // animating cells individually would desync the two. WAAPI (not a keyed CSS
  // animation) so the canvas element is never remounted, and rapid retriggers
  // (search-as-you-type) restart cleanly via cancel(). Values are deliberately
  // subtle so at typing cadence it reads as a soft pulse, not a strobe.
  _animateRefresh = () => {
    const el = this.contentRef.current;
    if (!el || typeof el.animate !== 'function') return;
    this._refreshAnim?.cancel();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._refreshAnim = el.animate(
      reduce
        ? [{ opacity: 0.5 }, { opacity: 1 }]
        : [
          { opacity: 0.4, transform: 'translateY(0.5cqw)' },
          { opacity: 1, transform: 'none' },
        ],
      { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  };

  // rAF-throttled scroll handler. Updates scrollTop in state on row crossings
  // so render re-windows the slice (paddings adjust). Native browser scroll
  // handles the smooth pixel-by-pixel motion of the tiles in between. No JS
  // transform is involved on the DOM side.
  handleScroll = () => {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null;
      const container = this.scrollRef.current;
      if (!container) return;
      const scrollTop = container.scrollTop;
      const rh = this.state.rowHeight || 1;
      const row = Math.floor(scrollTop / rh);
      if (row !== this._lastWindowRow) {
        this._lastWindowRow = row;
        this.setState({ scrollTop }, this._emitViewChange);
      }
    });
  };

  _emitViewChange = () => {
    const { cols, rowHeight, scrollTop } = this.state;
    const topIndex = (cols > 0 && rowHeight > 0)
      ? Math.max(0, Math.floor(scrollTop / rowHeight)) * cols
      : 0;
    this.props.onViewChange?.({ scrollTop, cols, rowHeight, topIndex });
  };

  // Imperative: scroll so the given grid row sits at the top.
  scrollToRow = (row) => {
    const container = this.scrollRef.current;
    const { rowHeight } = this.state;
    if (!container || !rowHeight) return;
    container.scrollTop = row * rowHeight;
  };

  // Imperative: scroll back to the top and zero the in-state scrollTop so the
  // virtualizer's next render starts at row 0. Consumers call this on filter
  // changes so the user sees the freshly filtered set from the top.
  resetScroll = () => {
    if (this.scrollRef.current) this.scrollRef.current.scrollTop = 0;
    this._lastWindowRow = 0;
    if (this.state.scrollTop !== 0) this.setState({ scrollTop: 0 });
  };

  render() {
    const { items, getItemKey, getItemFoil, renderTile, renderFooter, getItemCellStyle, measureFooter, gridSize = 'auto' } = this.props;
    const allItems = items || [];

    // Windowing: render only the tiles whose row sits within the viewport (plus
    // a small buffer). On first paint cols/rowHeight are 0. We render an initial
    // batch so tiles exist in the DOM and componentDidMount can measure them.
    const { cols, rowHeight, scrollTop, viewport, viewportW } = this.state;
    const measured = cols > 0 && rowHeight > 0 && viewport > 0;
    const overflow = this._overflowPx();
    const vpad = this._vpadPx();

    let firstIdx = 0;
    let lastIdx;
    let topPad = vpad;
    let bottomPad = vpad;
    let firstRow = 0;
    let lastRow = 0;
    let totalRows = 0;

    if (measured) {
      totalRows = Math.ceil(allItems.length / cols);
      firstRow = Math.max(0, Math.floor(scrollTop / rowHeight) - BUFFER_ROWS);
      lastRow = Math.min(totalRows - 1, Math.ceil((scrollTop + viewport) / rowHeight) + BUFFER_ROWS);
      firstIdx = firstRow * cols;
      lastIdx = Math.min(allItems.length, (lastRow + 1) * cols);
      // Padding-based virtualization: the browser lays the slice into the
      // grid at the right vertical position via paddingTop, and reserves the
      // rest of the scroll height via paddingBottom. Native scrolling moves
      // the whole grid. No JS transforms involved.
      topPad = vpad + firstRow * rowHeight;
      bottomPad = vpad + Math.max(0, (totalRows - lastRow - 1) * rowHeight);
    } else {
      // Enough to fill any reasonable viewport so we can measure one.
      lastIdx = Math.min(allItems.length, 40);
    }
    const slice = allItems.slice(firstIdx, lastIdx);

    // Hidden measurement probe: a real `.card-grid` so the browser computes the
    // live cols / gap / tile size (incl. media-query + gridSize override). Each
    // cell MIRRORS the real grid item. The `.deck-card-tile` card plus the
    // consumer's footer mirror (measureFooter). So firstTile.offsetHeight is
    // the FULL row item height (card + footer). The inner card carries
    // `.deck-card-tile-inner` so the image area is measurable.
    const probeStyle = { visibility: 'hidden', height: 0, overflow: 'hidden', pointerEvents: 'none' };
    if (gridSize !== 'auto') probeStyle.gridTemplateColumns = `repeat(${gridSize}, 1fr)`;
    const probe = (
      <div ref={this.gridRef} className="card-grid" style={probeStyle} aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i}>
            <div className="deck-card-tile">
              <div className="deck-card-tile-inner h-full w-full rounded-card" />
            </div>
            {measureFooter || null}
          </div>
        ))}
      </div>
    );

    // Build the foil quad descriptors for the currently-mounted slice; the rAF
    // loop reads this each frame to position the canvas quads (with live tilt +
    // scrollTop). Only FOIL items get a quad; non-foil keep their DOM <img>.
    const visibleFoil = [];

    const { entering } = this.state;
    const entranceBase = typeof this.props.entranceStagger === 'number' ? this.props.entranceStagger : 0;

    const cells = slice.map((item, i) => {
      const gi = firstIdx + i;
      const key = getItemKey(item, gi);
      const foil = getItemFoil ? getItemFoil(item) : null;
      const imageHidden = !!foil;

      if (foil) {
        visibleFoil.push({ key, gi, imageUrl: foil.imageUrl || '', zones: foil.zones });
      }

      const onTilt = imageHidden ? ((t) => { this._tilts.set(key, t); }) : undefined;

      // Consumer-driven per-cell style: unowned graying, hover/highlight
      // zIndex, etc. Applied to the grid cell so it covers both the card and
      // its footer.
      const cellStyle = getItemCellStyle ? (getItemCellStyle(item, { index: gi }) || {}) : {};

      const tileChild = renderTile(item, { key, index: gi, imageHidden, onTilt, isHovered: false });
      const footerChild = renderFooter ? renderFooter(item, { key, index: gi }) : null;

      // Foil cells get a ref so _refreshFoilTops can read their real offsetTop
      // (the foil quad rides the DOM, not a uniform row model).
      const cellRef = foil
        ? (el) => { if (el) this._foilCells.set(key, el); else this._foilCells.delete(key); }
        : undefined;

      return (
        // alignSelf: 'start' keeps the cell at its intrinsic height instead of
        // being stretched to the row track height. Without it, a renderTile
        // that wraps its content in `h-full` (e.g. PileSearchDialog's relative
        // h-full wrapper) inflates to (tile + footer) px, pushing the footer
        // an extra footer-height below the card.
        // minWidth: 0 collapses the grid item's auto minimum so `repeat(N, 1fr)`
        // (== minmax(auto, 1fr)) tracks size purely by 1fr. A nowrap/truncate
        // footer (e.g. the pick-mode hint) reports its full line as min-content
        // and would otherwise force every column wide and overflow the panel.
        // The entrance is a FADE, not a rise. Foil cells hole out and their art
        // is drawn by the WebGL canvas behind, which tracks each cell's live
        // opacity (see _buildFoilCards). It cannot track a transform, because
        // the quads are positioned from offsetTop, which transforms don't move.
        // A translate here would slide every non-foil card while the foil cards
        // stayed pinned.
        <div
          key={key}
          ref={cellRef}
          className={entering ? 'stagger-fade-in' : undefined}
          style={{
            alignSelf: 'start',
            minWidth: 0,
            ...(entering ? { '--stagger': `${entranceBase + Math.min(i, ENTRANCE_STEP_CAP) * ENTRANCE_STEP_MS}ms` } : null),
            ...cellStyle,
          }}
        >
          {tileChild}
          {footerChild}
        </div>
      );
    });

    // Stash the foil descriptor list for the rAF loop (built above during the
    // map). The loop turns these into canvas-local quad rects every frame.
    this._visibleFoil = visibleFoil;

    const gridStyle = { paddingTop: `${topPad}px`, paddingBottom: `${bottomPad}px` };
    if (gridSize !== 'auto') gridStyle.gridTemplateColumns = `repeat(${gridSize}, 1fr)`;

    // Foil canvas anchor + size. The canvas is a scrolled content child anchored
    // at the windowed region's top content-y (= topPad, the first rendered row's
    // top) and covers exactly the windowed rows plus FOIL_OVERFLOW headroom on
    // each edge (for a hovered card's lift/tilt). It rides native scroll with the
    // tiles, and re-anchors here whenever the window changes (row crossing), so
    // its quads stay content-locked without per-frame scrollTop tracking. Quad
    // y's in _buildFoilCards are measured relative to this._foilAnchorTop.
    const foilAnchorTop = topPad;
    this._foilAnchorTop = foilAnchorTop;
    const windowRows = measured ? (lastRow - firstRow + 1) : 0;
    const canvasH = (measured ? windowRows * rowHeight : viewport) + 2 * overflow;

    return (
      <div
        ref={this.scrollRef}
        // Hosts embedding the grid in a tight surface (create-deck
        // avatar picker) can slim the default side gutters.
        className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${this.props.scrollPadClassName || 'pl-10 pr-10'}`}
        onScroll={this.handleScroll}
      >
        {probe}
        {allItems.length === 0 ? null : (
          // Relative wrapper = the scroll content (height = the grid's full
          // padded height). The grid scrolls natively inside it; the foil canvas
          // is an absolutely-positioned content child anchored at the windowed
          // region, so it scrolls WITH the content on the same compositor pass.
          <div ref={this.contentRef} style={{ position: 'relative' }}>
            {/* WebGL foil overlay. A scrolled content child, anchored at the
                windowed region's top content-y (foilAnchorTop). It rides native
                scroll with the tiles (same compositor pass → frame-locked, no
                main-thread lag) and re-anchors on each row crossing so the macOS
                "big canvas drifts off the grid" failure mode can't accumulate.
                Covers the windowed rows + FOIL_OVERFLOW so a hovered lift/tilt
                isn't clipped. pointer-events:none so clicks reach the DOM tiles;
                zIndex:1 keeps the foil art over each tile's transparent hole.

                This layer is NEVER faded as a whole. It holds every foil card
                on screen, so one fade would light them all at a single moment
                and each card's foil would arrive after the card. The effect
                reads as a filter switching on late, which breaks the illusion
                that the card IS foil. Each quad fades individually instead,
                tracking its own cell (see _buildFoilCards). */}
            <div
              style={{ position: 'absolute', top: `${foilAnchorTop}px`, left: 0, width: 0, height: 0, zIndex: 1, pointerEvents: 'none' }}
            >
              <CardFoilScene
                ref={this.foilSceneRef}
                width={viewportW + 2 * overflow}
                height={canvasH}
                inset={overflow}
                paramOverrides={GRID_FOIL_OVERRIDES}
              />
            </div>
            {/* The actual tile grid. Plain CSS Grid with padding-based
                virtualization. Browser scrolls natively, no JS transforms. */}
            <div className="card-grid" style={gridStyle}>
              {cells}
            </div>
          </div>
        )}
      </div>
    );
  }
}
